import type { TFunction } from "i18next";
import {
  ContractOverviewFigure,
  FrameworkRole,
  type ContractOverviewFigureDto,
  type ContractOverviewScopeDto,
  type ContractReportQueryDto,
} from "../../autogen/bff-api";
import { urls } from "../../urls";
import { base64Encode } from "../../common/utils/base64";

export type ContractScope = "all" | "frameworkAgreements";

export type OverviewSelection = { scope: ContractScope; projectId: string | null };

export const overviewQuery = (organizationId: string, selection: OverviewSelection): ContractReportQueryDto => ({
  owningOrganizationId: organizationId,
  ...(selection.scope === "frameworkAgreements"
    ? { frameworkRoles: [FrameworkRole.FrameworkSupplierContract], activeFrameworkAgreement: true }
    : {}),
  ...(selection.projectId ? { projectIds: [selection.projectId] } : {}),
});

export const registerLink = (base: ContractReportQueryDto, figure: Pick<ContractOverviewFigureDto, "filter">): string => {
  const filter = Object.fromEntries(Object.entries(figure.filter).filter(([, value]) => value != null));
  const query: ContractReportQueryDto = { ...base, statuses: [], ...filter, limit: 10, skip: 0 };
  return `${urls.app.contracts.viewAll}?q=${base64Encode(JSON.stringify(query))}`;
};

export const ANNUAL_VALUE_COLUMNS = ["title", "counterparty", "annualValue", "endDate", "responsible"];

export const figureLink = (base: ContractReportQueryDto, figure: ContractOverviewFigureDto): string =>
  figure.figure === ContractOverviewFigure.AnnualValue
    ? `${registerLink(base, figure)}&columns=${base64Encode(JSON.stringify(ANNUAL_VALUE_COLUMNS))}`
    : registerLink(base, figure);

export const FIGURE_ORDER: ContractOverviewFigure[] = [
  ContractOverviewFigure.Active,
  ContractOverviewFigure.AnnualValue,
  ContractOverviewFigure.SignedLast30Days,
  ContractOverviewFigure.AwaitingSignature,
  ContractOverviewFigure.ExpiringWithin90Days,
  ContractOverviewFigure.OptionDeadlineWithin90Days,
  ContractOverviewFigure.WithoutActiveOwner,
];

export const figureLabel = (t: TFunction, figure: ContractOverviewFigure, scope: ContractScope = "all"): string =>
  ({
    [ContractOverviewFigure.Active]:
      scope === "frameworkAgreements" ? t("Suppliers in active framework agreements") : t("Active contracts"),
    [ContractOverviewFigure.AnnualValue]: t("Total annual value"),
    [ContractOverviewFigure.SignedLast30Days]: t("Signed in the last 30 days"),
    [ContractOverviewFigure.AwaitingSignature]: t("Awaiting signing"),
    [ContractOverviewFigure.ExpiringWithin90Days]: t("Expiring within 90 days"),
    [ContractOverviewFigure.OptionDeadlineWithin90Days]: t("Option deadline within 90 days"),
    [ContractOverviewFigure.WithoutActiveOwner]: t("Without an active owner"),
  })[figure];

export const figureDefinition = (t: TFunction, figure: ContractOverviewFigure, scope: ContractScope): string =>
  ({
    [ContractOverviewFigure.Active]:
      scope === "frameworkAgreements"
        ? t(
            "Supplier contracts in framework agreements that are active: not terminated and not past their end date, whether or not the supplier contract is published."
          )
        : t("Contracts whose start date has passed and whose end date has not, or that have no end date."),
    [ContractOverviewFigure.AnnualValue]: t(
      "The sum of the annual value of the active contracts: the price per year, or the price per month times twelve."
    ),
    [ContractOverviewFigure.SignedLast30Days]: t(
      "Contracts where the last required signature came in during the last 30 days. Only the first signed document of a contract counts, and contracts uploaded already signed are not counted."
    ),
    [ContractOverviewFigure.AwaitingSignature]: t(
      "Contracts with a document sent for signing that is not fully signed, also those waiting for attachments. Cancelled signings are not counted."
    ),
    [ContractOverviewFigure.ExpiringWithin90Days]: t("Active contracts whose end date is within the next 90 days."),
    [ContractOverviewFigure.OptionDeadlineWithin90Days]: t(
      "Contracts with an option that is not exercised and whose deadline to give notice or exercise it is within the next 90 days."
    ),
    [ContractOverviewFigure.WithoutActiveOwner]: t(
      "Active contracts where the responsible person is missing, deactivated or no longer in the organisation."
    ),
  })[figure];

export const contractScopeText = (t: TFunction, scope: ContractOverviewScopeDto): string => {
  if (scope.onlySharedContracts) return String(t("the contracts shared with you"));
  if (scope.onlyOwnContracts) return String(t("the contracts you created or are responsible for"));
  if (scope.organizationWide) return String(t("the whole organisation"));
  if (scope.departmentNames.length === 0) return String(t("the contracts shared with you"));
  const departments = scope.departmentNames.join(", ");
  if (scope.alsoSharedContracts) return String(t("{{departments}}, and contracts shared with you", { departments }));
  return departments;
};
